import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useQuery, useLazyQuery } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import { GET_ALL_FIELDS_V2 } from '@nile/banyan/src/Graphql/Mutations/ticket';
import {
  ALL_SHARD_MODULES,
  AppContext,
  DromoUploader,
  getTranslatedText
} from '@nile/components';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';
import {
  callQuery,
  getQuery,
  getTicketImportFields,
  getTicketValidators,
  shapeOptions
} from '../Fields/ticketFields';
import {
  getFieldDescription,
  getFieldType,
  getSelectOptions,
  getValidators,
  intlConverter
} from '../utils/helpers';
import { ImportIdentifier } from '../constants';

const TicketUploader = (props) => {
  const { userToken, activator, onResults, onCancel } = props;
  const intl = useIntl();

  const appContext = useContext(AppContext);
  const { currentProduct } = appContext;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);
  const [ticketCustomFields, setTicketCustomFields] = useState([]);

  const [
    getCustomFields,
    { loading: cfLoading, error: cfError, data: customFields }
  ] = useLazyQuery(GET_ALL_FIELDS_V2, {
    client: AccountClient,
    variables: { input: ALL_SHARD_MODULES.ticket },
    fetchPolicy: 'cache-and-network',
    onCompleted({ getAllFields }) {
      if (getAllFields) {
        setTicketCustomFields(getAllFields);

        const sortedFields = [...getAllFields].sort(
          (a, b) =>
            (a.fieldCategory === 'DEFAULT' ? 0 : 1) -
            (b.fieldCategory === 'DEFAULT' ? 0 : 1)
        );

        const customFieldsToAdd = sortedFields.map((cf) => {
          const fieldDescription = getFieldDescription(intl, cf, true);
          const fieldDataObj = {
            label: cf.label,
            key: cf.columnName,
            type:
              cf?.fieldType === 'DATE'
                ? [
                    'date',
                    {
                      displayFormat: 'yyyy-MM-dd'
                    }
                  ]
                : getFieldType(cf),
            selectOptions: getSelectOptions(cf, true),
            validators: cf?.fieldType === 'DATE' ? [] : getTicketValidators(cf)
          };
          if (fieldDescription) {
            fieldDataObj.description = fieldDescription;
          }
          return fieldDataObj;
        });
        setFields((prev) =>
          intlConverter(intl, [...prev, ...customFieldsToAdd])
        );
      }
    }
  });

  useEffect(() => {
    setLoading(true);
    const ticketsImportFields = getTicketImportFields(currentProduct);
    const initFields = [...ticketsImportFields];

    const selectFields = ticketsImportFields.reduce((acc, field, idx) => {
      if (field.type === 'select') {
        acc[idx] = getQuery(field.key)
          ? callQuery(field.key)
          : field.selectOptions;
      }
      return acc;
    }, {});

    const promises = Object.values(selectFields);
    Promise.all(promises)
      .then((options) => {
        options.forEach((set, idx) => {
          const indexToModify = Object.keys(selectFields)[idx];
          initFields[indexToModify].selectOptions = shapeOptions(
            initFields[indexToModify].key,
            set
          );
        });
        setFields([...intlConverter(intl, initFields)]);
        getCustomFields();
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(true);
        console.log(e);
      });
  }, []);

  const dependentFields = useMemo(() => {
    return ticketCustomFields.filter((item) => item.parentField !== null);
  }, [ticketCustomFields]);

  const validationFieldsArray = [];

  const customFieldValidatorRowHook = ({ row, index }) => {
    const newRecord = { row, index };

    validationFieldsArray.forEach((key) => {
      // if (newRecord.row?.[key]) {
      newRecord.row[key].info = [];
      // }
    });

    function addError(key, errorKey) {
      if (!validationFieldsArray?.includes(key)) {
        validationFieldsArray.push(key);
      }
      // if (newRecord.row?.[key]) {
      newRecord.row[key].info = [
        {
          message: getTranslatedText(
            intl,
            errorKey || 'dromo.validation.requiredField'
          ),
          level: 'error'
        }
      ];
      // }
    }

    function addRequiredError(key) {
      addError(key, 'dromo.validation.requiredField');
    }

    if (dependentFields.length) {
      // checking the fields which are required if parentfield also required or not
      dependentFields.forEach((field) => {
        //checking the parent field required or not is already in configuration fields in ticketFields or not
        const alreadyIsRequired = fields
          .find(
            (f) =>
              f.key.trim().toLowerCase() ===
              field?.parentField?.label.trim().toLowerCase()
          )
          ?.validators?.some((v) => v.validate === 'required');
        if (
          newRecord.row[field?.columnName]?.resultValue &&
          field?.parentField?.columnName &&
          !newRecord.row[field?.parentField?.columnName]?.resultValue &&
          !alreadyIsRequired
        ) {
          addRequiredError(field.parentField.columnName);
        }

        if (
          newRecord.row[field?.parentField?.columnName]?.resultValue &&
          field?.options
        ) {
          const validOptions = field?.options.filter(
            (option) =>
              option.parentOption?.value ===
              newRecord.row[field?.parentField?.columnName].resultValue
          );

          if (newRecord.row[field?.columnName]) {
            newRecord.row[field?.columnName].selectOptions = getSelectOptions(
              { ...field, options: validOptions },
              true
            );
          }
        }
      });
    }

    return newRecord;
  };

  function addParentFieldRecursive(instance, cf) {
    if (!cf) return;

    const fieldDescription = getFieldDescription(intl, cf, true);
    const fieldDataObj = {
      label: cf.label,
      key: cf.columnName,
      type: getFieldType(cf),
      selectOptions: getSelectOptions(cf, true),
      validators: getValidators(cf)
    };

    if (fieldDescription) {
      fieldDataObj.description = fieldDescription;
    }

    instance.addField(fieldDataObj);

    // Recurse on the parentField if it exists
    if (cf.parentField) {
      const parentField = ticketCustomFields.find(
        (field) => field.id === cf?.parentField?.id
      );
      addParentFieldRecursive(instance, parentField);
    }
  }

  const handleUploadStep = (instance, data) => {
    const columns = data.dataPreview?.[0] || [];
    if (dependentFields.length) {
      dependentFields.forEach((col) => {
        if (
          columns.some(
            (colXl) =>
              colXl.trim().toLowerCase() === col?.label?.trim().toLowerCase()
          ) &&
          !columns.some(
            (colXl) =>
              colXl.trim().toLowerCase() ===
              col?.parentField?.label?.trim().toLowerCase()
          )
        ) {
          // Initial call
          const cf = ticketCustomFields.find(
            (field) => field.id === col?.parentField?.id
          );
          addParentFieldRecursive(instance, cf);
        }
      });
    }
  };

  const handleReviewStep = (instance, data) => {
    const uploadedColumnsName = Object.values(data.headerMapping);
    if (dependentFields.length) {
      dependentFields.map((col) => {
        if (
          uploadedColumnsName.includes(col?.columnName) &&
          !uploadedColumnsName.includes(col?.parentField?.columnName)
        ) {
          // Initial call
          const cf = ticketCustomFields.find(
            (field) => field.id === col?.parentField?.id
          );
          addParentFieldRecursive(instance, cf);
        }
      });
    }
  };

  if (loading || cfLoading) return <Loader loading />;

  if (error) return null;

  return (
    <DromoUploader
      settings={{
        importIdentifier: ImportIdentifier.TICKET,
        title: getTranslatedText(intl, 'migrate.data.ticket.title')
      }}
      fields={fields}
      rowHooks={[(row) => row, customFieldValidatorRowHook]}
      columnHooks={[(column) => column]}
      stepHooks={[
        // { type: 'uploadStep', callback: handleUploadStep },
        { type: 'reviewStep', callback: handleReviewStep }
      ]}
      onResults={(response, metadata) => {
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="workspace" />
            </div>
            <FormattedMessage id="migrate.data.ticket.title" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default TicketUploader;
